import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Truck, Plus, X, Archive } from "lucide-react";
import { supplierFormSchema, type SupplierFormInput } from "@finora/validation";
import type { SupplierDto } from "@finora/types";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

function SupplierFormModal({
  supplier,
  onClose,
}: {
  supplier: SupplierDto | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SupplierFormInput>({
    resolver: zodResolver(supplierFormSchema),
    defaultValues: {
      name: supplier?.name ?? "",
      address: supplier?.address ?? "",
      city: supplier?.city ?? "",
      postalCode: supplier?.postalCode ?? "",
      email: supplier?.email ?? "",
      phone: supplier?.phone ?? "",
      website: supplier?.website ?? "",
      taxId: supplier?.taxId ?? "",
      registrationNumber: supplier?.registrationNumber ?? "",
      bankAccount: supplier?.bankAccount ?? "",
    },
  });

  const save = useMutation({
    mutationFn: (data: SupplierFormInput) =>
      supplier ? api.updateSupplier(supplier.id, data) : api.createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <form
        onSubmit={handleSubmit((data) => save.mutate(data))}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-auto rounded-xl bg-surface shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {supplier ? "Izmena dobavljača" : "Novi dobavljač"}
          </h2>
          <button
            type="button"
            aria-label="Zatvori"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="block text-sm font-medium text-slate-700">Naziv firme *</label>
            <input
              {...register("name")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">PIB</label>
              <input
                {...register("taxId")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Matični broj</label>
              <input
                {...register("registrationNumber")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Tekući račun</label>
            <input
              {...register("bankAccount")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Adresa</label>
            <input
              {...register("address")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Mesto</label>
              <input
                {...register("city")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Poštanski broj</label>
              <input
                {...register("postalCode")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Email</label>
            <input
              {...register("email")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Telefon</label>
              <input
                {...register("phone")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Sajt</label>
              <input
                {...register("website")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Došlo je do greške prilikom čuvanja. Pokušajte ponovo.
            </p>
          )}
        </div>

        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" className="flex-1" disabled={isSubmitting || save.isPending}>
            Sačuvaj
          </Button>
        </div>
      </form>
    </div>
  );
}

export function SuppliersTab() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierDto | null>(null);

  const { data: suppliers, isLoading } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => api.listSuppliers(),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.archiveSupplier(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["suppliers"] }),
  });

  function openCreate() {
    setEditingSupplier(null);
    setModalOpen(true);
  }

  function openEdit(supplier: SupplierDto) {
    setEditingSupplier(supplier);
    setModalOpen(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden />
          Novi dobavljač
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !suppliers || suppliers.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={Truck} title="Još nema dobavljača" description="Dodajte prvog dobavljača dugmetom iznad." />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Naziv</th>
                <th>PIB</th>
                <th>Telefon</th>
                <th>Email</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50">
                  <td className="font-medium text-slate-800" onClick={() => openEdit(s)}>
                    {s.name}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(s)}>
                    {s.taxId ?? "—"}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(s)}>
                    {s.phone ?? "—"}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(s)}>
                    {s.email ?? "—"}
                  </td>
                  <td>
                    <button
                      aria-label="Arhiviraj dobavljača"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Arhivirati dobavljača "${s.name}"?`)) {
                          archive.mutate(s.id);
                        }
                      }}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Archive className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <SupplierFormModal supplier={editingSupplier} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}
